/**
 * Cinematic slide transition engine.
 *
 * Transitions run on two stacked slide layers (outgoing + incoming) with the
 * Web Animations API. Only transform / opacity are animated for most effects
 * so the browser can keep them on the compositor; blur, reveal and spotlight
 * also touch filter / clip-path and are marked `heavy` so low-power devices
 * fall back to a plain fade.
 */

export const EASINGS = [
  { id: "smooth", label: "Smooth", css: "cubic-bezier(0.4, 0, 0.2, 1)" },
  { id: "easeInOut", label: "Ease In-Out", css: "cubic-bezier(0.65, 0, 0.35, 1)" },
  { id: "spring", label: "Spring", css: "cubic-bezier(0.34, 1.32, 0.64, 1)" },
  { id: "cinematic", label: "Cinematic", css: "cubic-bezier(0.77, 0, 0.18, 1)" },
];

export const easingCss = (id) =>
  (EASINGS.find((e) => e.id === id) || EASINGS[0]).css;

const pct = (n) => `${n}%`;

/**
 * Each transition returns keyframes for the outgoing and incoming layer.
 * `dir` is 1 when moving forward and -1 when moving back, so directional
 * effects reverse naturally.
 */
export const TRANSITIONS = [
  {
    id: "none",
    label: "None",
    description: "Switch instantly",
    build: () => null,
  },
  {
    id: "fade",
    label: "Smooth Fade",
    description: "Elegant crossfade between slides",
    build: () => ({
      out: [{ opacity: 1 }, { opacity: 0 }],
      in: [{ opacity: 0 }, { opacity: 1 }],
    }),
  },
  {
    id: "zoom",
    label: "Cinematic Zoom",
    description: "Camera glides into the next slide",
    build: (dir) => ({
      out: [
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0, transform: `scale(${dir > 0 ? 1.28 : 0.84})` },
      ],
      in: [
        { opacity: 0, transform: `scale(${dir > 0 ? 0.84 : 1.28})` },
        { opacity: 1, transform: "scale(1)" },
      ],
    }),
  },
  {
    id: "morph",
    label: "Morph",
    description: "Matching elements glide into place",
    // Real morph is handled by morph.js; this is the layer-level fallback.
    build: () => ({
      out: [{ opacity: 1 }, { opacity: 0 }],
      in: [{ opacity: 0 }, { opacity: 1 }],
    }),
  },
  {
    id: "cube",
    label: "3D Cube",
    description: "Slides rotate like the faces of a cube",
    perspective: 2600,
    build: (dir, { width }) => {
      const half = width / 2;
      const turn = dir > 0 ? -90 : 90;
      return {
        out: [
          { transform: `translateZ(-${half}px) rotateY(0deg) translateZ(${half}px)`, filter: "brightness(1)" },
          { transform: `translateZ(-${half}px) rotateY(${turn}deg) translateZ(${half}px)`, filter: "brightness(0.6)" },
        ],
        in: [
          { transform: `translateZ(-${half}px) rotateY(${-turn}deg) translateZ(${half}px)`, filter: "brightness(0.6)" },
          { transform: `translateZ(-${half}px) rotateY(0deg) translateZ(${half}px)`, filter: "brightness(1)" },
        ],
      };
    },
  },
  {
    id: "parallax",
    label: "Parallax",
    description: "Background and content move at different speeds",
    build: (dir) => ({
      out: [
        { transform: "translateX(0)", opacity: 1 },
        { transform: `translateX(${pct(-35 * dir)})`, opacity: 0.4 },
      ],
      in: [{ transform: `translateX(${pct(100 * dir)})` }, { transform: "translateX(0)" }],
      inner: {
        // Backdrop trails behind the content inside the incoming slide.
        "[data-cine-backdrop]": [
          { transform: `translateX(${pct(-55 * dir)})` },
          { transform: "translateX(0)" },
        ],
      },
    }),
  },
  {
    id: "depth",
    label: "Depth Zoom",
    description: "Layers move with perspective and depth",
    build: (dir) => ({
      out: [
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0, transform: `scale(${dir > 0 ? 0.88 : 1.12})` },
      ],
      in: [
        { opacity: 0, transform: `scale(${dir > 0 ? 1.1 : 0.9})` },
        { opacity: 1, transform: "scale(1)" },
      ],
      inner: {
        "[data-cine-content]": [
          { transform: `scale(${dir > 0 ? 1.08 : 0.94})`, opacity: 0 },
          { transform: "scale(1)", opacity: 1, offset: 1 },
        ],
      },
    }),
  },
  {
    id: "reveal",
    label: "Slide Reveal",
    description: "Next slide appears through a directional wipe",
    heavy: true,
    inOnTop: true,
    build: (dir) => ({
      out: [{ transform: "translateX(0)" }, { transform: `translateX(${pct(-12 * dir)})` }],
      in: [
        { clipPath: dir > 0 ? "inset(0 0 0 100%)" : "inset(0 100% 0 0)" },
        { clipPath: "inset(0 0 0 0)" },
      ],
    }),
  },
  {
    id: "blur",
    label: "Blur Dissolve",
    description: "Previous slide blurs while the next sharpens",
    heavy: true,
    build: () => ({
      out: [
        { opacity: 1, filter: "blur(0px)" },
        { opacity: 0, filter: "blur(16px)" },
      ],
      in: [
        { opacity: 0, filter: "blur(16px)" },
        { opacity: 1, filter: "blur(0px)" },
      ],
    }),
  },
  {
    id: "spotlight",
    label: "Spotlight",
    description: "A soft circular reveal introduces the next slide",
    heavy: true,
    inOnTop: true,
    build: () => ({
      out: [{ filter: "brightness(1)" }, { filter: "brightness(0.45)" }],
      in: [
        { clipPath: "circle(0% at 50% 50%)" },
        { clipPath: "circle(75% at 50% 50%)" },
      ],
    }),
  },
  {
    id: "pan",
    label: "Camera Pan",
    description: "Smooth camera movement across the deck",
    build: (dir) => ({
      out: [
        { transform: "translateX(0) scale(1)" },
        { transform: `translateX(${pct(-50 * dir)}) scale(0.94)`, offset: 0.5 },
        { transform: `translateX(${pct(-100 * dir)}) scale(1)` },
      ],
      in: [
        { transform: `translateX(${pct(100 * dir)}) scale(1)` },
        { transform: `translateX(${pct(50 * dir)}) scale(0.94)`, offset: 0.5 },
        { transform: "translateX(0) scale(1)" },
      ],
    }),
  },
  {
    id: "flip",
    label: "Flip 3D",
    description: "A subtle 3D page rotation",
    perspective: 2200,
    build: (dir) => ({
      out: [
        { opacity: 1, transform: "rotateY(0deg) translateX(0)" },
        { opacity: 0, transform: `rotateY(${-28 * dir}deg) translateX(${pct(-8 * dir)})` },
      ],
      in: [
        { opacity: 0, transform: `rotateY(${28 * dir}deg) translateX(${pct(8 * dir)})` },
        { opacity: 1, transform: "rotateY(0deg) translateX(0)" },
      ],
    }),
  },
  {
    id: "film",
    label: "Film Dissolve",
    description: "Cinematic opacity with a soft light bloom",
    build: () => ({
      out: [
        { opacity: 1, filter: "brightness(1)" },
        { opacity: 0.6, filter: "brightness(1.35)", offset: 0.55 },
        { opacity: 0, filter: "brightness(1.6)" },
      ],
      in: [
        { opacity: 0, filter: "brightness(1.6)", transform: "scale(1.02)" },
        { opacity: 0.7, filter: "brightness(1.25)", offset: 0.5 },
        { opacity: 1, filter: "brightness(1)", transform: "scale(1)" },
      ],
    }),
  },
];

export const getTransitionDef = (id) =>
  TRANSITIONS.find((t) => t.id === id) || TRANSITIONS[1];

export const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (error) {
    return false;
  }
};

/** Rough capability check: very low core / memory counts get static effects. */
export const isLowPowerDevice = () => {
  try {
    const cores = navigator.hardwareConcurrency || 8;
    const memory = navigator.deviceMemory || 8;
    return cores <= 2 || memory <= 2;
  } catch (error) {
    return false;
  }
};

const canAnimate = (el) => Boolean(el && typeof el.animate === "function");

/**
 * Run a transition between two layer elements.
 * Returns a controller: { finished: Promise, finish(): void }.
 * `finish()` jumps to the end (used when the presenter clicks again mid-transition).
 */
export const runTransition = ({
  outEl,
  inEl,
  stageEl,
  type = "fade",
  duration = 650,
  easing = "smooth",
  direction = 1,
  width = 1280,
  height = 720,
  lowPower = false,
}) => {
  const reduced = prefersReducedMotion();
  let def = getTransitionDef(type);
  if (lowPower && def.heavy) def = getTransitionDef("fade");
  const frames = def.build(direction, { width, height });

  if (reduced || !frames || !canAnimate(inEl) || duration <= 0) {
    return { finished: Promise.resolve(), finish: () => {} };
  }

  const timing = {
    duration,
    easing: easingCss(easing),
    fill: "both",
  };
  const animations = [];
  const restore = [];

  if (def.perspective && stageEl) {
    const prev = stageEl.style.perspective;
    stageEl.style.perspective = `${def.perspective}px`;
    restore.push(() => {
      stageEl.style.perspective = prev;
    });
  }

  const layerStyle = (el, z) => {
    if (!el) return;
    const prevZ = el.style.zIndex;
    const prevWill = el.style.willChange;
    const prevBackface = el.style.backfaceVisibility;
    el.style.zIndex = String(z);
    el.style.willChange = "transform, opacity";
    if (def.perspective) el.style.backfaceVisibility = "hidden";
    restore.push(() => {
      el.style.zIndex = prevZ;
      el.style.willChange = prevWill;
      el.style.backfaceVisibility = prevBackface;
    });
  };
  layerStyle(outEl, def.inOnTop ? 1 : 2);
  layerStyle(inEl, def.inOnTop ? 2 : 1);

  if (outEl && canAnimate(outEl) && frames.out) animations.push(outEl.animate(frames.out, timing));
  if (frames.in) animations.push(inEl.animate(frames.in, timing));
  if (frames.inner) {
    Object.entries(frames.inner).forEach(([selector, keyframes]) => {
      inEl.querySelectorAll(selector).forEach((node) => {
        if (canAnimate(node)) animations.push(node.animate(keyframes, timing));
      });
    });
  }

  let settled = false;
  const cleanup = () => {
    if (settled) return;
    settled = true;
    animations.forEach((a) => {
      try {
        a.cancel();
      } catch (error) {
        /* already gone */
      }
    });
    restore.forEach((fn) => fn());
  };

  const finished = Promise.all(animations.map((a) => a.finished.catch(() => null))).then(cleanup);

  return {
    finished,
    finish: () => {
      animations.forEach((a) => {
        try {
          a.finish();
        } catch (error) {
          /* ignore */
        }
      });
      cleanup();
    },
  };
};
